
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useAdminAuth = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [adminRole, setAdminRole] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkAttempted, setCheckAttempted] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const checkAdminStatus = useCallback(async () => {
    // Don't check again if we've already checked
    if (checkAttempted) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Check if user is authenticated
      const { data: session, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Session error:', sessionError);
        setIsAuthenticated(false);
        setAdminRole(null);
        setCheckAttempted(true);
        setIsLoading(false);
        setError(sessionError);
        return;
      }
      
      if (!session.session) {
        setIsAuthenticated(false);
        setAdminRole(null);
        setCheckAttempted(true);
        setIsLoading(false);
        navigate("/auth");
        return;
      }

      // User is authenticated
      setIsAuthenticated(true);

      try {
        // Directly query the admin_users table - with the new RLS policy this should work
        const { data: adminData, error: adminError } = await supabase
          .from('admin_users')
          .select('*')
          .eq('user_id', session.session.user.id)
          .maybeSingle();
        
        if (adminError) {
          console.error('Error checking admin status:', adminError);
          setAdminRole(null);
          setError(adminError);
          navigate("/dashboard"); // Redirect to user dashboard if error checking admin status
        } else if (!adminData) {
          console.log('User is not an admin, redirecting to user dashboard');
          setAdminRole(null);
          navigate("/dashboard");
        } else {
          // User is an admin
          console.log('User is an admin, setting role:', adminData.role);
          setAdminRole(adminData.role);
        }
      } catch (error) {
        console.error('Error in admin data query:', error);
        setAdminRole(null);
        setError(error instanceof Error ? error : new Error('Unknown error checking admin status'));
        navigate("/dashboard"); // Redirect to user dashboard on error
      }
      
    } catch (error) {
      console.error('Error checking admin status:', error);
      setAdminRole(null);
      setError(error instanceof Error ? error : new Error('Unknown error'));
    } finally {
      setCheckAttempted(true);
      setIsLoading(false);
    }
  }, [navigate, checkAttempted]);

  useEffect(() => {
    // Check admin status on mount
    checkAdminStatus();
    
    // Set up auth state listener
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        setIsAuthenticated(false);
        setAdminRole(null);
        navigate("/auth");
      } else if (event === 'SIGNED_IN' && session) {
        // When user signs in, check admin status
        setIsAuthenticated(true);
        setCheckAttempted(false); // Reset check so we force a recheck
        checkAdminStatus();
      }
    });
    
    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [checkAdminStatus, navigate]);

  return { 
    checkAdminStatus, 
    isLoading, 
    adminRole, 
    isAuthenticated,
    checkAttempted,
    error
  };
};
